import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCredits } from '@/hooks/useCredits';
import { useMembership } from '@/hooks/useMembership';

interface ToolOverride {
  action_type: string;
  credit_cost: number;
  is_unlimited: boolean;
  is_locked: boolean;
}

interface ToolAccessResult {
  isLocked: boolean;
  isUnlimited: boolean;
  creditCost: number;
  canAccess: boolean;
}

// Default credit costs for tools (fallback if not in database)
const DEFAULT_CREDIT_COSTS: Record<string, number> = {
  'resume-tailoring': 5,
  'resume-analysis': 1,
  'job-wizard': 1,
  'metrics-wizard': 3,
  'linkedin-wizard': 5,
  'solution-asset': 3,
  'career-clarity-report': 3,
  'find-hiring-manager': 2,
  'job-check': 1,
  'job-finder-hub': 0,
  'find-track-jobs': 0,
};

/**
 * Hook to manage tool access based on membership plan and database overrides.
 * 
 * Tool states per plan:
 * - Locked (is_locked = true): Tool completely unavailable
 * - Unlimited (is_unlimited = true): Free access, no credit cost
 * - Credit Cost (credit_cost > 0): Deducts credits on use
 */
export function useToolAccess() {
  const { plan, balance, isLoading: creditsLoading } = useCredits();
  const { isPaid, isFree, isLoading: membershipLoading } = useMembership();
  const [overrides, setOverrides] = useState<ToolOverride[]>([]);
  const [isLoadingOverrides, setIsLoadingOverrides] = useState(true);

  // Fetch plan-specific overrides
  useEffect(() => {
    const fetchOverrides = async () => {
      if (!plan?.id) {
        setIsLoadingOverrides(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('plan_credit_overrides')
          .select('action_type, credit_cost, is_unlimited, is_locked')
          .eq('plan_id', plan.id);

        if (error) {
          console.error('Error fetching tool overrides:', error);
        } else {
          setOverrides(data || []);
        }
      } catch (err) {
        console.error('Failed to fetch tool overrides:', err);
      } finally {
        setIsLoadingOverrides(false);
      }
    };

    fetchOverrides();
  }, [plan?.id]);

  /**
   * Get access information for a specific tool
   */
  const getToolAccess = useCallback((toolId: string): ToolAccessResult => {
    // Find override for this tool
    const override = overrides.find(o => o.action_type === toolId);
    
    // Determine if tool is explicitly locked via database
    const isExplicitlyLocked = override?.is_locked ?? false;
    
    // Determine if tool has unlimited access
    const isUnlimited = override?.is_unlimited ?? false;
    
    // Get credit cost (from override or default)
    const creditCost = override?.credit_cost ?? DEFAULT_CREDIT_COSTS[toolId] ?? 1;
    
    // Determine access:
    // - If explicitly locked in database → locked
    // - If paid member → always has access
    // - If unlimited → has access
    // - If free with credits and creditCost is covered → has access
    // - Otherwise → locked for free users
    
    let canAccess = false;
    let isLocked = false;
    
    if (isExplicitlyLocked) {
      // Tool is explicitly locked for this plan
      isLocked = true;
      canAccess = false;
    } else if (isPaid) {
      // Paid members have access (unless explicitly locked)
      isLocked = false;
      canAccess = true;
    } else if (isUnlimited) {
      // Tool is free for this plan
      isLocked = false;
      canAccess = true;
    } else if (isFree) {
      // Free users: can access if they have enough credits
      if (balance >= creditCost && creditCost > 0) {
        isLocked = false;
        canAccess = true;
      } else if (creditCost === 0) {
        // Free tools
        isLocked = false;
        canAccess = true;
      } else {
        // Not enough credits
        isLocked = true;
        canAccess = false;
      }
    }
    
    return {
      isLocked,
      isUnlimited,
      creditCost,
      canAccess,
    };
  }, [overrides, isPaid, isFree, balance]);

  /**
   * Check if a tool is locked (for simple boolean checks)
   */
  const isToolLocked = useCallback((toolId: string): boolean => {
    if (creditsLoading || membershipLoading || isLoadingOverrides) {
      return false; // Don't show as locked while loading
    }
    return getToolAccess(toolId).isLocked;
  }, [getToolAccess, creditsLoading, membershipLoading, isLoadingOverrides]);

  const isLoading = creditsLoading || membershipLoading || isLoadingOverrides;

  return {
    getToolAccess,
    isToolLocked,
    isLoading,
    overrides,
  };
}

export default useToolAccess;
